import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FiSmartphone, FiPlus, FiTrash2, FiEdit2, FiCheck, FiX,
  FiCreditCard, FiKey, FiBluetooth, FiWifi, FiCpu, FiHardDrive,
  FiLock, FiUnlock, FiRefreshCw, FiLink, FiShield, FiAlertCircle
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../services/api';
import { useToast } from '../hooks/useToast';
import './LinkedDevicesManager.css';
import { apiErrorMessage } from '../utils/apiError';

const DEVICE_TYPE_ICONS = {
  smart_card: FiCreditCard,
  yubikey: FiKey,
  bluetooth: FiBluetooth,
  wifi: FiWifi,
  router: FiCpu,
  switch: FiHardDrive,
  nfc: FiSmartphone,
  custom: FiCpu
};

const DEVICE_TYPE_COLORS = {
  smart_card: '#3b82f6',
  yubikey: '#10b981',
  bluetooth: '#6366f1',
  wifi: '#f59e0b',
  router: '#ef4444',
  switch: '#8b5cf6',
  nfc: '#06b6d4',
  custom: '#888'
};

function LinkedDevicesManager({ onClose }) {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();

  const showErrorRef = useRef(showError);
  const tRef = useRef(t);
  const isMountedRef = useRef(true);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => () => { isMountedRef.current = false; }, []);

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masterPasswordInfo, setMasterPasswordInfo] = useState({ enabled: false, hasPassword: false });

  // Add device form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDevice, setNewDevice] = useState({ deviceId: '', deviceType: 'smart_card', deviceName: '' });
  const [addingDevice, setAddingDevice] = useState(false);

  // Edit device
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  // Master password form
  const [showMasterPwForm, setShowMasterPwForm] = useState(false);
  const [masterPwData, setMasterPwData] = useState({ password: '', confirm: '' });
  const [savingMasterPw, setSavingMasterPw] = useState(false);

  const fetchDevices = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/api/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        if (!isMountedRef.current) return;
        setDevices(response.data.devices);
        setMasterPasswordInfo(response.data.masterPassword);
      }
    } catch (err) {
      showErrorRef.current(tRef.current('devices.errorLoading'));
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const handleAddDevice = async (e) => {
    e.preventDefault();
    if (!newDevice.deviceId.trim()) { showError(t('devices.deviceIdRequired')); return; }
    setAddingDevice(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}/api/devices/link`, {
        deviceId: newDevice.deviceId.trim(),
        deviceType: newDevice.deviceType,
        deviceName: newDevice.deviceName.trim() || undefined
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.success) {
        success(t('devices.deviceLinked'));
        setNewDevice({ deviceId: '', deviceType: 'smart_card', deviceName: '' });
        setShowAddForm(false);
        fetchDevices();
      }
    } catch (err) {
      showError(apiErrorMessage(err, t, 'devices.errorLinking'));
    } finally { setAddingDevice(false); }
  };

  const handleDeleteDevice = async (id, name) => {
    if (!window.confirm(t('devices.confirmUnlink', { name }))) return;
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_BASE}/api/devices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      success(t('devices.deviceUnlinked'));
      fetchDevices();
    } catch (err) {
      showError(t('devices.errorUnlinking'));
    }
  };

  const handleRenameDevice = async (id) => {
    if (!editName.trim()) return;
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_BASE}/api/devices/${id}`, 
        { deviceName: editName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      success(t('devices.deviceRenamed'));
      setEditingId(null);
      fetchDevices();
    } catch (err) {
      showError(t('devices.errorRenaming'));
    }
  };

  const handleSetupMasterPassword = async (e) => {
    e.preventDefault();
    const { password, confirm } = masterPwData;
    if (!password || password.length < 8) {
      showError(t('devices.passwordTooShort'));
      return;
    }
    if (password !== confirm) {
      showError(t('devices.passwordMismatch'));
      return;
    }
    setSavingMasterPw(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${API_BASE}/api/devices/master-password/setup`, 
        { masterPassword: password, enabled: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      success(t('devices.masterPasswordSet'));
      setMasterPwData({ password: '', confirm: '' });
      setShowMasterPwForm(false);
      fetchDevices();
    } catch (err) {
      showError(t('devices.errorSettingPassword'));
    } finally { setSavingMasterPw(false); }
  };

  const handleDisableMasterPassword = async () => {
    if (!window.confirm(t('devices.confirmDisableMasterPw'))) return;
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${API_BASE}/api/devices/master-password/setup`,
        { enabled: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      success(t('devices.masterPasswordDisabled'));
      fetchDevices();
    } catch (err) {
      showError(t('devices.errorDisablingPassword'));
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString(undefined, {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const deviceTypes = [
    { value: 'smart_card', label: t('login.deviceTypes.smartCard') },
    { value: 'yubikey', label: t('login.deviceTypes.yubikey') },
    { value: 'bluetooth', label: t('login.deviceTypes.bluetooth') },
    { value: 'wifi', label: t('login.deviceTypes.wifi') },
    { value: 'router', label: t('login.deviceTypes.router') },
    { value: 'switch', label: t('login.deviceTypes.switch') },
    { value: 'nfc', label: t('login.deviceTypes.nfc') },
    { value: 'custom', label: t('login.deviceTypes.custom') }
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-devices" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><FiLink /> {t('devices.title')}</h2>
          <button onClick={onClose}><FiX /></button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="devices-loading"><FiRefreshCw className="spin" /> {t('devices.loading')}</div>
          ) : (
            <>
              {/* Master Password Section */}
              <div className="devices-section">
                <div className="section-header">
                  <h3><FiShield size={16} /> {t('devices.masterPassword')}</h3>
                </div>
                
                <div className="master-pw-status">
                  {masterPasswordInfo.enabled ? (
                    <div className="mp-badge mp-enabled">
                      <FiLock size={16} />
                      <span>{t('devices.masterPasswordEnabled')}</span>
                      <button className="btn-text-danger" onClick={handleDisableMasterPassword}>
                        <FiUnlock size={14} /> {t('devices.disable')}
                      </button>
                      <button className="btn-text" onClick={() => setShowMasterPwForm(true)}>
                        <FiEdit2 size={14} /> {t('devices.change')}
                      </button>
                    </div>
                  ) : (
                    <div className="mp-badge mp-disabled">
                      <FiUnlock size={16} />
                      <span>{t('devices.masterPasswordDisabledLabel')}</span>
                      <button className="btn-text-primary" onClick={() => setShowMasterPwForm(true)}>
                        <FiLock size={14} /> {t('devices.enable')}
                      </button>
                    </div>
                  )}
                </div>

                {showMasterPwForm && (
                  <form className="mp-form" onSubmit={handleSetupMasterPassword}>
                    <div className="mp-form-group">
                      <label>{t('devices.newMasterPassword')}</label>
                      <input
                        type="password"
                        value={masterPwData.password}
                        onChange={(e) => setMasterPwData({ ...masterPwData, password: e.target.value })}
                        placeholder="••••••••"
                        minLength={8}
                      />
                    </div>
                    <div className="mp-form-group">
                      <label>{t('devices.confirmPassword')}</label>
                      <input
                        type="password"
                        value={masterPwData.confirm}
                        onChange={(e) => setMasterPwData({ ...masterPwData, confirm: e.target.value })}
                        placeholder="••••••••"
                      />
                    </div>
                    <div className="mp-form-note">
                      <FiAlertCircle size={14} />
                      <span>{t('devices.masterPasswordNote')}</span>
                    </div>
                    <div className="mp-form-actions">
                      <button type="submit" className="btn-primary-small" disabled={savingMasterPw}>
                        {savingMasterPw ? <FiRefreshCw className="spin" /> : <FiCheck />} {t('devices.save')}
                      </button>
                      <button type="button" className="btn-secondary-small" onClick={() => {
                        setShowMasterPwForm(false);
                        setMasterPwData({ password: '', confirm: '' });
                      }}>
                        {t('devices.cancel')}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Linked Devices Section */}
              <div className="devices-section">
                <div className="section-header">
                  <h3><FiSmartphone size={16} /> {t('devices.linkedDevices')} ({devices.length})</h3>
                  <button className="btn-icon-add" onClick={() => setShowAddForm(!showAddForm)}>
                    <FiPlus /> {t('devices.addDevice')}
                  </button>
                </div>

                {showAddForm && (
                  <form className="add-device-form" onSubmit={handleAddDevice}>
                    <div className="add-form-row">
                      <div className="add-form-field">
                        <label>{t('login.deviceType')}</label>
                        <select
                          value={newDevice.deviceType}
                          onChange={(e) => setNewDevice({ ...newDevice, deviceType: e.target.value })}
                        >
                          {deviceTypes.map(dt => (
                            <option key={dt.value} value={dt.value}>{dt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="add-form-field">
                        <label>{t('devices.deviceIdLabel')}</label>
                        <input
                          type="text"
                          value={newDevice.deviceId}
                          onChange={(e) => setNewDevice({ ...newDevice, deviceId: e.target.value })}
                          placeholder={t('devices.deviceIdPlaceholder')}
                        />
                      </div>
                    </div>
                    <div className="add-form-row">
                      <div className="add-form-field full">
                        <label>{t('devices.deviceNameLabel')}</label>
                        <input
                          type="text"
                          value={newDevice.deviceName}
                          onChange={(e) => setNewDevice({ ...newDevice, deviceName: e.target.value })}
                          placeholder={t('devices.deviceNamePlaceholder')}
                        />
                      </div>
                    </div>
                    <div className="add-form-actions">
                      <button type="submit" className="btn-primary-small" disabled={addingDevice}>
                        {addingDevice ? <FiRefreshCw className="spin" /> : <FiPlus />} {t('devices.link')}
                      </button>
                      <button type="button" className="btn-secondary-small" onClick={() => setShowAddForm(false)}>
                        {t('devices.cancel')}
                      </button>
                    </div>
                  </form>
                )}

                {devices.length === 0 ? (
                  <div className="no-devices">
                    <FiSmartphone size={32} />
                    <p>{t('devices.noDevices')}</p>
                    <span>{t('devices.noDevicesDesc')}</span>
                  </div>
                ) : (
                  <div className="devices-list">
                    {devices.map(device => {
                      const Icon = DEVICE_TYPE_ICONS[device.deviceType] || FiCpu;
                      const color = DEVICE_TYPE_COLORS[device.deviceType] || '#888';
                      const typeLabel = deviceTypes.find(dt => dt.value === device.deviceType)?.label || device.deviceType;

                      return (
                        <div key={device.id} className="device-card">
                          <div className="device-icon" style={{ color, background: `${color}18` }}>
                            <Icon size={22} />
                          </div>
                          <div className="device-info">
                            {editingId === device.id ? (
                              <div className="device-edit-name">
                                <input
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  autoFocus
                                  onKeyDown={(e) => e.key === 'Enter' && handleRenameDevice(device.id)}
                                />
                                <button className="btn-icon-success-sm" onClick={() => handleRenameDevice(device.id)}><FiCheck /></button>
                                <button className="btn-icon-sm" onClick={() => setEditingId(null)}><FiX /></button>
                              </div>
                            ) : (
                              <div className="device-name">{device.deviceName}</div>
                            )}
                            <div className="device-meta">
                              <span className="device-type-badge" style={{ color, borderColor: `${color}60` }}>
                                {typeLabel}
                              </span>
                              <span className="device-id-snippet">{device.deviceId.substring(0, 12)}...</span>
                            </div>
                            {device.lastUsed && (
                              <div className="device-last-used">
                                {t('devices.lastUsed')}: {formatDate(device.lastUsed)}
                              </div>
                            )}
                          </div>
                          <div className="device-actions">
                            <button className="btn-icon-sm" title={t('devices.rename')} onClick={() => {
                              setEditingId(device.id);
                              setEditName(device.deviceName);
                            }}>
                              <FiEdit2 size={14} />
                            </button>
                            <button className="btn-icon-danger-sm" title={t('devices.unlink')} onClick={() => handleDeleteDevice(device.id, device.deviceName)}>
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LinkedDevicesManager;
